/**
 * Table Management Tab - Admin Panel
 * Manages tables 1-20 for dine-in orders
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Users2, 
  MapPin, 
  RefreshCw,
  Check,
  X,
  ShoppingBag,
  UtensilsCrossed,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

export default function TableManagementTab({ token }) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);

  const fetchTables = useCallback(async () => {
    try {
      const response = await api.get('/tables');
      setTables(response.data.tables || []);
    } catch (error) {
      console.error('Error fetching tables:', error);
      toast.error('Failed to load tables');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTables();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchTables, 30000);
    return () => clearInterval(interval);
  }, [fetchTables]);

  const handleToggleActive = async (tableNumber, currentActive) => {
    try {
      await api.put(`/tables/${tableNumber}`, { is_active: !currentActive });
      toast.success(currentActive ? 'Table disabled' : 'Table enabled');
      await fetchTables();
    } catch (error) {
      console.error('Error toggling table:', error);
      toast.error('Failed to update table');
    }
  };

  const handleClearTable = async (tableNumber) => {
    try {
      await api.post(`/tables/${tableNumber}/clear`);
      toast.success('Table cleared');
      await fetchTables();
    } catch (error) {
      console.error('Error clearing table:', error);
      toast.error('Failed to clear table');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  // Count stats
  const activeTables = tables.filter(t => t.is_active);
  const tablesWithOrders = tables.filter(t => t.has_orders);
  const totalOrders = tables.reduce((sum, t) => sum + (t.order_count || 0), 0);
  const totalAmount = tables.reduce((sum, t) => sum + (t.total_amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Table Management</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Tables 1-20 for dine-in orders
          </p>
        </div>
        <button
          onClick={fetchTables}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{activeTables.length}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Active Tables</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Users2 className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{tablesWithOrders.length}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">With Orders</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <ShoppingBag className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{totalOrders}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Open Orders</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <UtensilsCrossed className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">${totalAmount.toFixed(2)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Unpaid</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tables Grid */}
      <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-10 gap-3">
        {tables.map((table) => (
          <TableCard
            key={table.table_number}
            table={table}
            onSelect={() => setSelectedTable(table)}
            onToggle={handleToggleActive}
          />
        ))}
      </div>

      {/* Table Detail Modal */}
      {selectedTable && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${selectedTable.has_orders ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    <span className={`text-xl font-bold ${selectedTable.has_orders ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      {selectedTable.table_number}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Table {selectedTable.table_number}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedTable.location} - Capacity: {selectedTable.capacity}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedTable(null)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Orders List */}
              {selectedTable.orders && selectedTable.orders.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Current Orders ({selectedTable.orders.length})
                  </h4>
                  {selectedTable.orders.map((order, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
                          #{order.id?.slice(0, 8).toUpperCase()}
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          order.order_type === 'dine-in' 
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                        }`}>
                          {order.order_type === 'dine-in' ? 'Dine-in' : 'Takeaway'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {order.items?.length || 0} items - ${order.total?.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Status: {order.status}
                      </div>
                    </div>
                  ))}
                  
                  <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between text-lg font-bold">
                      <span className="text-gray-900 dark:text-white">Total</span>
                      <span className="text-green-600 dark:text-green-400">
                        ${selectedTable.total_amount?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <UtensilsCrossed className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No active orders</p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={() => handleToggleActive(selectedTable.table_number, selectedTable.is_active)}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedTable.is_active
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {selectedTable.is_active ? 'Disable Table' : 'Enable Table'}
              </button>
              {selectedTable.has_orders && (
                <button
                  onClick={() => {
                    handleClearTable(selectedTable.table_number);
                    setSelectedTable(null);
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Clear Table
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TableCard({ table, onSelect, onToggle }) {
  const hasOrders = table.has_orders;
  const isActive = table.is_active;
  
  return (
    <button
      onClick={onSelect}
      className={`aspect-square rounded-xl border-2 flex flex-col items-center justify-center transition-all hover:scale-105 ${
        !isActive 
          ? 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 opacity-50'
          : hasOrders
            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 shadow-lg'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-green-500'
      }`}
    >
      <span className={`text-2xl font-bold ${
        !isActive 
          ? 'text-gray-400'
          : hasOrders 
            ? 'text-orange-600 dark:text-orange-400' 
            : 'text-gray-700 dark:text-gray-300'
      }`}>
        {table.table_number}
      </span>
      {hasOrders && (
        <span className="text-xs text-orange-600 dark:text-orange-400 font-medium mt-1">
          {table.order_count} order{table.order_count !== 1 ? 's' : ''}
        </span>
      )}
      {!isActive && (
        <span className="text-xs text-gray-400 mt-1">Off</span>
      )}
    </button>
  );
}
